import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Container, LinearProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { localGet } from '../../../utils/localStorageUtils';
import {
  TEMPLATE_CSV, chunkByStudent, csvToScheduleRows, schedulesSummary, uploadSchedules,
} from '../../../utils/schedulesService';

/**
 * Class schedules for middle and high school students (Nov 2026). DiscloSure admins upload the
 * district's schedule export (one row per class). Stored on the server only; responders see
 * the class the student was in when an emergency was reported (class, room, teacher), on
 * EMERGENCY reports only.
 * The Clever SIS sync fills the same store automatically once connected.
 */
const SchedulesPanel = ({ data }) => {
  const theme = useTheme();
  const fileRef = useRef(null);
  const [user, setUser] = useState(null);
  const [summary, setSummary] = useState(null);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const district = data?.id;
  useEffect(() => { (async () => setUser(await localGet('user', true)))(); }, []);
  const isAdmin = user?.permission === 'disclosureAdmin';

  const refresh = async () => {
    if (!district) return;
    const s = await schedulesSummary(district);
    if (s.ok) setSummary(s);
  };
  useEffect(() => { if (isAdmin) refresh(); /* eslint-disable-next-line */ }, [isAdmin, district]);

  if (!isAdmin) return null;

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'disclosure-class-schedules-template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setError(''); setResult(null);
    const { rows, missing } = csvToScheduleRows(await file.text());
    if (missing.length) { setError(`The spreadsheet needs ${missing.join(' and ')}.`); return; }
    if (!rows.length) { setError('The spreadsheet has no class rows.'); return; }
    const totals = { received: 0, students: 0, classes: 0, unmatched: [], empty: [] };
    const chunks = chunkByStudent(rows);
    let done = 0;
    setProgress({ done: 0, total: rows.length });
    for (const chunk of chunks) {
      const res = await uploadSchedules(district, chunk.map(({ _row, ...r }) => r));
      if (!res.ok) {
        setError(`${res.message} (${done} rows were saved before this).`);
        break;
      }
      totals.received += res.received;
      totals.students += res.students;
      totals.classes += res.classes;
      // Server row numbers are positions in this request; map back to the sheet's rows.
      totals.unmatched.push(...res.unmatched.map((n) => chunk[n - 1]?._row).filter(Boolean));
      totals.empty.push(...res.empty.map((n) => chunk[n - 1]?._row).filter(Boolean));
      done += chunk.length;
      setProgress({ done, total: rows.length });
    }
    totals.unmatched.sort((a, b) => a - b);
    totals.empty.sort((a, b) => a - b);
    setProgress(null);
    setResult(totals);
    refresh();
  };

  const border = `1px solid ${theme.palette.custom?.gray || '#ccc'}`;
  const muted = { fontSize: '14px', color: theme.palette.text?.secondary || '#666', lineHeight: 1.5 };
  const listRows = (nums) => (nums.length > 20 ? `${nums.slice(0, 20).join(', ')} and ${nums.length - 20} more` : nums.join(', '));

  return (
    <Container sx={{ display: 'flex', flexDirection: 'column', marginLeft: '60px', marginRight: '60px', marginTop: '40px',
                     width: 'auto', paddingLeft: '0px !important', border, borderRadius: '4px' }}>
      <Box sx={{ borderBottom: border }}>
        <Typography sx={{ fontSize: '20px', fontWeight: 700, margin: '20px' }}>Class schedules (middle and high school)</Typography>
      </Box>
      <Box sx={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Typography sx={muted}>
          Upload the district's class schedules, one row per class: period, course, room, teacher, start and end
          time, and (optional) days such as MWF or A Day. Students are matched by email (or student number), and
          each upload replaces that student's schedule. Schedules are stored on the server only. Responders see
          the student's class, room and teacher at the time of the report, on emergency reports only.
        </Typography>
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
          {summary ? `${summary.studentsWithSchedules} students have a schedule on file`
                     + (summary.studentsWithSchedules ? ` (${summary.withTimes} with class times)` : '')
                     + (summary.lastUpdated ? `, last updated ${new Date(summary.lastUpdated).toLocaleDateString()}` : '')
                   : 'Loading…'}
        </Typography>
        <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <Button variant="contained" disabled={!!progress} onClick={() => fileRef.current && fileRef.current.click()}
                  sx={{ textTransform: 'none', boxShadow: 'none' }}>
            Upload schedules (.csv)
          </Button>
          <Button variant="outlined" onClick={downloadTemplate} sx={{ textTransform: 'none' }}>
            Download template
          </Button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={onFile} />
        </Box>
        {progress && (
          <Box>
            <LinearProgress variant="determinate" value={(progress.done / progress.total) * 100} />
            <Typography sx={muted}>Uploading {progress.done} of {progress.total} rows…</Typography>
          </Box>
        )}
        {result && (
          <Box>
            <Typography sx={{ fontSize: '14px' }}>
              Saved <strong>{result.classes}</strong> classes for <strong>{result.students}</strong> students
              ({result.received} rows read).
            </Typography>
            {result.unmatched.length > 0 && (
              <Typography sx={muted}>Not matched to a student in this district (sheet rows): {listRows(result.unmatched)}</Typography>
            )}
            {result.empty.length > 0 && (
              <Typography sx={muted}>Matched but had no course or period (sheet rows): {listRows(result.empty)}</Typography>
            )}
          </Box>
        )}
        {error && <Typography sx={{ fontSize: '13px', color: theme.palette.error?.main || '#d32f2f' }}>{error}</Typography>}
      </Box>
    </Container>
  );
};

export default SchedulesPanel;
